import { useState, useEffect } from "react";
import { supabase } from "../auth/supabaseClient";
import { useAuth } from "../../contexts/AuthContext";
import { useInView } from "react-intersection-observer";

import { FiExternalLink } from "react-icons/fi";
import {
  AiOutlineLoading3Quarters,
  AiOutlineCheckCircle,
} from "react-icons/ai";

import moment from "moment";
import { ethers } from "ethers";

function FeedAwardCard(props) {
  const [numLikes, setNumLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [identityString, setIdentityString] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState(
    "https://ribbon-public-assets.s3.amazonaws.com/temp-image.png"
  );

  useEffect(() => {
    if (props.award) {
      getIdentity();
    }
  }, [props.award]);

  const downloadProfileImage = async (photoUrl) => {
    try {
      const { data, error } = await supabase.storage
        .from("avatars")
        .download(photoUrl);
      if (error) {
        throw error;
      }
      const url = URL.createObjectURL(data);
      setProfileImageUrl(url);
    } catch (error) {
      console.log("Error downloading image: ", error.message);
    }
  };

  async function getIdentity() {
    // look up and return profile associated with eth address
    if (props.award.eth_address) {
      console.log("hit");
      const { data: addressData, error: addressError } = await supabase
        .from("addresses")
        .select("*")
        .eq("address", props.award.eth_address)
        .single();

      if (addressData) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", addressData.user_id)
          .single();

        if (profileData) {
          console.log("before", profileData);
          setIdentityString(
            profileData.first_name + " " + profileData.last_name
          );
          downloadProfileImage(profileData.photo_url);
        }
      } else {
        // resolve ETH address to ENS name
        const provider = new ethers.providers.JsonRpcProvider(
          process.env.REACT_APP_ALCHEMY_RPC_URL
        );
        let resolvedEns = await provider.lookupAddress(props.award.eth_address);

        if (resolvedEns) {
          setIdentityString(resolvedEns);
        } else {
          // first and last part of eth address
          let ethAddress = props.award.eth_address;
          let ethAddressDisplay =
            ethAddress.substring(0, 5) + "..." + ethAddress.substring(38);
          setIdentityString(ethAddressDisplay);
        }
      }
    } else if (props.award.email) {
      // this is incomplete
      // we need to look up the profile associated with the email
      setIdentityString(props.award.email);
    }
  }

  useEffect(() => {
    fetchLikes();
  }, []);

  function fetchLikes() {
    supabase
      .from("award_likes")
      .select("*")
      .eq("award_id", props.award.id)
      .then((res) => {
        setNumLikes(res.data.length);
        // check if user already liked
        res.data.forEach((like) => {
          if (like.user_id == props.user.id) {
            setIsLiked(true);
          }
        });
      });
  }

  function openShareLink() {
    let shareLink =
      process.env.REACT_APP_RIBBON_PLATFORM_URL + "/award/" + props.award.id;
    window.open(shareLink, "_blank");
  }

  return (
    <div className="max-w-md min-w-md w-100 px-4 py-4 bg-white shadow-md rounded-lg">
      <div className="py-2 flex flex-row items-center justify-between">
        <div className="flex flex-row items-center">
          <a
            href="#"
            className="flex flex-row items-center focus:outline-none focus:shadow-outline rounded-lg"
          >
            <img
              className="rounded-full h-8 w-8 object-cover"
              src={profileImageUrl}
              alt=""
            />
            <p className="ml-2 text-base font-medium">{identityString}</p>
          </a>
        </div>
        <div className="flex flex-row items-center">
          <p className="text-xs font-semibold text-gray-500">
            {moment(props.award.created_at).fromNow()}{" "}
          </p>
        </div>
      </div>
      <div className="py-2">
        <p className="leading-snug text-purpler font-bold">
          {props.award.award_title}
        </p>
        <p className="leading-snug">{props.award.description}</p>
      </div>
      <div className="flex justify-between">
        <div className="py-2">
          <button
            onClick={() => {
              if (isLiked) {
                props.removeLike(props.award.id);
                setIsLiked(false);
                setNumLikes(numLikes - 1);
              } else {
                props.addLike(props.award.id);
                setIsLiked(true);
                setNumLikes(numLikes + 1);
              }
            }}
            className="flex flex-row items-center focus:outline-none focus:shadow-outline rounded-lg"
          >
            <svg
              fill={isLiked ? "true" : "none"}
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              className="w-5 h-5"
            >
              <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
            </svg>
            <span className="ml-1">{numLikes}</span>
          </button>
        </div>
        <div className="py-2">
          <button onClick={() => openShareLink()}>
            <FiExternalLink />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Feed() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [awards, setAwards] = useState([]);

  // section related to infinite scroll
  const pageSize = 10;
  const [pageLocation, setPageLocation] = useState({ start: 0, end: pageSize });
  const [noMoreAwards, setNoMoreAwards] = useState(false);
  const [ref, inView] = useInView({});

  useEffect(() => {
    if (inView) {
      console.log("in view", pageLocation);
      setPageLocation({
        start: 0,
        end: pageLocation.end + pageSize,
      });
    }
  }, [inView]);

  useEffect(() => {
    fetchAwards();
  }, [pageLocation]);

  // change loading message if no more awards to load
  useEffect(() => {
    if (awards.length < pageLocation.end) {
      setNoMoreAwards(true);
    }
  }, [awards, pageLocation]);

  async function addLike(award_id) {
    console.log("user id", user.id);
    const { error } = await supabase
      .from("award_likes")
      .insert([{ user_id: user.id, award_id: award_id }]);
    if (error) {
      console.log("error", error);
    }
  }

  async function removeLike(award_id) {
    const { error } = await supabase
      .from("award_likes")
      .delete()
      .eq("user_id", user.id)
      .eq("award_id", award_id);
    if (error) {
      console.log("error", error);
    }
  }

  async function fetchAwards() {
    const { data } = await supabase
      .from("awards")
      .select("*")
      .order("created_at", { ascending: false })
      .range(pageLocation.start, pageLocation.end);

    console.log("awards: ", data);
    setAwards(data);
  }

  function createAwardCards() {
    return awards.map((award) => {
      return (
        <FeedAwardCard
          key={award.id}
          user={user}
          addLike={addLike}
          removeLike={removeLike}
          award={award}
        />
      );
    });
  }

  useEffect(() => {
    fetchAwards();
    setLoading(false);
  }, []);

  return (
    <div className="grid grid-cols-1 gap-6 my-6 px-4 md:px-6 lg:px-8">
      {createAwardCards()}
      <div ref={ref}>
        {!noMoreAwards && (
          <AiOutlineLoading3Quarters size={40} className="animate-spin" />
        )}
      </div>
      {noMoreAwards && (
        <div className="text-center text-gray-500 flex items-center justify-center mb-4">
          <AiOutlineCheckCircle className="inline mr-1" />
          <p>no more awards to load</p>
        </div>
      )}
    </div>
  );
}
